import { useState } from "react";
import { useNavigate } from "react-router";
import type { Dashboard } from "@/services/ApiClient";
import { Screen } from "../components/Screen";
import { useApp } from "../container";
import { useQuery } from "../hooks/useQuery";
import { NotFoundScreen } from "./NotFoundScreen";
import styles from "./StatsScreen.module.css";

type Audience = "all" | "bots" | "humans";
const AUDIENCES: Audience[] = ["humans", "bots", "all"];
const TITLE: Record<Audience, string> = { all: "All devices", bots: "Bots", humans: "Humans" };
const TONE: Record<Audience, string> = { all: "var(--label2)", bots: "var(--red)", humans: "var(--tint)" };

/**
 * The owner's dashboard. The server answers only the admin's session; anyone
 * else gets a 404 and sees the same not-found page as for any unknown address.
 * Owner-only, so the copy is English and not part of the dictionaries.
 */
export function StatsScreen() {
  const app = useApp();
  const navigate = useNavigate();
  const data = useQuery<Dashboard | null>(() => app.account.withToken((token) => app.api.dashboard(token)).catch(() => null), []);
  if (data.data === undefined) return null;
  if (data.data === null) return <NotFoundScreen />;
  const d = data.data;
  return (
    <Screen title="Dashboard" back={() => navigate("/settings")}>
      <p className={styles.small}>Generated {fmtDateTime(d.generatedAt)} · this domain only</p>

      <section className={styles.section}>
        <h2 className={styles.heading}>Unique devices</h2>
        <div className={styles.grid}>
          {AUDIENCES.map((a) => (
            <div key={a} className={styles.card}>
              <span className={styles.label} style={{ color: TONE[a] }}>{TITLE[a]}</span>
              <span className={styles.big}>{d.devices[a].total}</span>
              <span className={styles.small}>30d {d.devices[a].last30} · 7d {d.devices[a].last7} · today {d.devices[a].today}</span>
            </div>
          ))}
        </div>
      </section>

      {AUDIENCES.map((a) => (
        <section key={a} className={styles.section}>
          <h2 className={styles.heading}>Returning · {TITLE[a]}</h2>
          <div className={styles.card}>
            <DailyChart points={d.daily[a]} tone={TONE[a]} />
            <RetentionTable rows={d.retention[a]} />
          </div>
        </section>
      ))}

      <section className={styles.section}>
        <h2 className={styles.heading}>Scenarios (humans, anonymous)</h2>
        <div className={`${styles.card} ${styles.scroll}`}>
          {d.scenarios.length === 0 ? <span className={styles.small}>No entries yet.</span> : (
            <table className={styles.table}>
              <thead><tr><th>Scenario</th><th className={styles.num}>Devices</th><th className={styles.num}>Entries</th><th className={styles.num}>30d</th><th className={styles.num}>7d</th><th className={styles.num}>Per device / week</th></tr></thead>
              <tbody>
                {d.scenarios.map((s) => (
                  <tr key={s.scenarioId}><td>{s.scenarioId}</td><td className={styles.num}>{s.devices}</td><td className={styles.num}>{s.entries}</td><td className={styles.num}>{s.entries30}</td><td className={styles.num}>{s.entries7}</td><td className={styles.num}>{s.weeklyPerDevice}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>Accounts · {d.accounts.total}</h2>
        <div className={styles.card}>
          <div className={styles.legend}>
            {Object.entries(d.accounts.byProvider).map(([provider, n]) => <span key={provider}>{provider} {n}</span>)}
          </div>
        </div>
        <div className={`${styles.card} ${styles.scroll}`}>
          {d.accounts.rows.length === 0 ? <span className={styles.small}>Nobody has signed in yet.</span> : (
            <table className={styles.table}>
              <thead><tr><th>Account</th><th>Sign-in</th><th>Since</th><th className={styles.num}>Archives</th><th className={styles.num}>Size</th><th>Last archive</th></tr></thead>
              <tbody>
                {d.accounts.rows.map((r) => (
                  <tr key={r.emailMasked + r.createdAt}><td>{r.emailMasked}</td><td>{r.providers.join(", ")}</td><td>{fmtDate(r.createdAt)}</td><td className={styles.num}>{r.archives}</td><td className={styles.num}>{fmtBytes(r.bytes)}</td><td>{r.lastArchiveAt ? fmtDateTime(r.lastArchiveAt) : "—"}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.heading}>Clicks (humans)</h2>
        <div className={styles.grid}>
          <Counts title="Tab bar" counts={d.clicks.tabs} />
          <Counts title="Calendar" counts={d.clicks.calendar} />
          <Counts title="Report periods" counts={d.clicks.reportPeriods} />
          <Counts title="Report sharing" counts={d.clicks.reportShares} />
          <Counts title="Language · auto" counts={d.clicks.localesAuto} />
          <Counts title="Language · chosen" counts={d.clicks.localesManual} />
        </div>
      </section>
    </Screen>
  );
}

function Counts({ title, counts }: { title: string; counts: Record<string, number> }) {
  const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...rows.map(([, n]) => n));
  return (
    <div className={styles.card}>
      <span className={styles.label}>{title}</span>
      {rows.length === 0 && <span className={styles.small}>—</span>}
      {rows.map(([key, n]) => (
        <div key={key}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8125rem" }}><span>{key}</span><span>{n}</span></div>
          <div className={styles.bar}><i style={{ width: `${(100 * n) / max}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function RetentionTable({ rows }: { rows: Dashboard["retention"][Audience] }) {
  return (
    <table className={styles.table}>
      <thead><tr><th>Came back within</th><th className={styles.num}>Cohort</th><th className={styles.num}>Returned</th><th style={{ width: "40%" }}>Share</th></tr></thead>
      <tbody>
        {rows.map((r) => {
          const pct = r.cohort > 0 ? Math.round((100 * r.returned) / r.cohort) : 0;
          return (
            <tr key={r.afterDays}><td>{r.afterDays} d</td><td className={styles.num}>{r.cohort}</td><td className={styles.num}>{r.returned}</td><td><div className={styles.bar}><i style={{ width: `${pct}%` }} /></div><span className={styles.small}>{r.cohort > 0 ? `${pct}%` : "no cohort yet"}</span></td></tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** New and returning devices per day for the last 30 days; hover a day for its numbers. */
function DailyChart({ points, tone }: { points: { day: string; fresh: number; returning: number }[]; tone: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720, H = 180, PAD = { top: 10, right: 12, bottom: 22, left: 36 };
  const innerW = W - PAD.left - PAD.right, innerH = H - PAD.top - PAD.bottom;
  const n = points.length;
  const max = Math.max(1, ...points.map((p) => Math.max(p.fresh, p.returning)));
  const x = (i: number) => PAD.left + (n <= 1 ? innerW / 2 : (innerW * i) / (n - 1));
  const y = (v: number) => PAD.top + innerH - (innerH * v) / max;
  const path = (key: "fresh" | "returning") => points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(" ");
  const totals = points.reduce((a, p) => ({ fresh: a.fresh + p.fresh, returning: a.returning + p.returning }), { fresh: 0, returning: 0 });
  const step = Math.max(1, Math.ceil(n / 6));
  const current = hover === null ? null : points[hover];
  return (
    <div className={styles.chart}>
      <div className={styles.legend}>
        <span><i style={{ background: tone }} />Returning {totals.returning}</span>
        <span><i style={{ background: "var(--label3)" }} />New {totals.fresh}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} onMouseLeave={() => setHover(null)}>
        {[0, Math.round(max / 2), max].map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke="var(--separator)" strokeWidth={0.5} />
            <text x={PAD.left - 6} y={y(v) + 4} fontSize={10} fill="var(--label2)" textAnchor="end">{v}</text>
          </g>
        ))}
        <path d={path("fresh")} fill="none" stroke="var(--label3)" strokeWidth={1.5} strokeDasharray="4 3" />
        <path d={path("returning")} fill="none" stroke={tone} strokeWidth={2} />
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + innerH} stroke="var(--separator)" />}
        {points.map((p, i) => (
          <g key={p.day}>
            {i % step === 0 && <text x={x(i)} y={H - PAD.bottom + 14} fontSize={10} fill="var(--label2)" textAnchor="middle">{p.day.slice(5)}</text>}
            <rect x={x(i) - innerW / Math.max(1, n - 1) / 2} y={PAD.top} width={innerW / Math.max(1, n - 1)} height={innerH} fill="transparent" onMouseEnter={() => setHover(i)} />
          </g>
        ))}
      </svg>
      {current && hover !== null && (
        <div className={styles.tip} style={{ left: `${(100 * x(hover)) / W}%`, top: 24 }}>
          {current.day} · returning {current.returning} · new {current.fresh}
        </div>
      )}
    </div>
  );
}

const fmtDate = (iso: string) => iso.slice(0, 10);
const fmtDateTime = (iso: string) => iso.slice(0, 16).replace("T", " ");
function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
